import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Convert a hex color (#RRGGBB) to [r, g, b] triple for jsPDF; returns fallback if unparseable.
function hexToRgb(hex, fallback = [10, 17, 40]) {
  if (!hex || typeof hex !== "string") return fallback;
  const m = hex.replace("#", "").match(/^([a-f0-9]{6})$/i);
  if (!m) return fallback;
  const v = m[1];
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}

/**
 * Generate the Master Search results PDF.
 * @param {object} args
 * @param {string} args.query
 * @param {object} args.settings       Site settings including pdf_logo_url, school_name, primary_color, etc.
 * @param {Array}  args.groups         [{ sport, types: { [typeName]: [records] } }, ...]
 * @param {number} args.totalCount
 */
export async function generateSearchResultsPdf({ query, settings = {}, groups = [], totalCount = 0 }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" }); // 612 x 792 pt
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;
  const primary = hexToRgb(settings.primary_color);
  const secondary = hexToRgb(settings.secondary_color, [225, 29, 72]);
  const accent = hexToRgb(settings.accent_color, [250, 204, 21]);

  // ---------- Header ----------
  let cursorY = 36;
  const logoUrl = settings.pdf_logo_url || settings.logo_url;
  let headerHeight = 60;
  if (logoUrl) {
    try {
      // Detect image format (jsPDF requires PNG/JPEG/WEBP). Assume PNG for data URLs unless JPEG.
      const isJpeg = /^data:image\/jpe?g/i.test(logoUrl) || /\.jpe?g(\?|$)/i.test(logoUrl);
      const fmt = isJpeg ? "JPEG" : "PNG";
      const img = await loadImage(logoUrl);
      const maxLogoHeight = 54;
      const maxLogoWidth = 180;
      const ratio = img.width / img.height;
      let h = maxLogoHeight, w = h * ratio;
      if (w > maxLogoWidth) { w = maxLogoWidth; h = w / ratio; }
      doc.addImage(logoUrl, fmt, marginX, cursorY, w, h);
      headerHeight = Math.max(headerHeight, h + 16);
    } catch (e) { /* ignore image load failures */ }
  }

  // Right-aligned block: school name, date
  const rightX = pageWidth - marginX;
  const schoolName = settings.school_name || settings.site_title || "Sports Record Book";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...primary);
  doc.text(schoolName, rightX, cursorY + 12, { align: "right" });
  if (settings.tagline) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(settings.tagline, rightX, cursorY + 26, { align: "right" });
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  doc.text(today, rightX, cursorY + 42, { align: "right" });

  cursorY += headerHeight;

  // Divider
  doc.setDrawColor(...accent);
  doc.setLineWidth(2.5);
  doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
  cursorY += 18;

  // ---------- Title block ----------
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...primary);
  doc.text("Master Search Results", marginX, cursorY);
  cursorY += 22;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  const summaryLine = query
    ? `Search: "${query}" — ${totalCount} record${totalCount === 1 ? "" : "s"} across ${groups.length} sport${groups.length === 1 ? "" : "s"}`
    : `${totalCount} records across ${groups.length} sports`;
  doc.text(summaryLine, marginX, cursorY);
  cursorY += 16;

  // If nothing to show
  if (groups.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text("No records found for this search.", marginX, cursorY + 20);
    doc.save(fileName(schoolName, query));
    return;
  }

  // ---------- Groups ----------
  for (const group of groups) {
    const sportName = group.sport?.name || "Unknown Sport";
    const sportPrimary = hexToRgb(group.sport?.primary_color || settings.primary_color, primary);
    const sportAccent = hexToRgb(group.sport?.accent_color || settings.accent_color, accent);

    // Page break if we're near the bottom
    if (cursorY > pageHeight - 120) { doc.addPage(); cursorY = 40; }

    // Sport header band
    const bandY = cursorY;
    const bandH = 26;
    doc.setFillColor(...sportPrimary);
    doc.rect(marginX, bandY, pageWidth - marginX * 2, bandH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(255, 255, 255);
    doc.text(sportName.toUpperCase(), marginX + 10, bandY + 17);

    // Count on right
    const sportTotal = Object.values(group.types).reduce((n, arr) => n + arr.length, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`${sportTotal} record${sportTotal === 1 ? "" : "s"}`, pageWidth - marginX - 10, bandY + 17, { align: "right" });
    cursorY = bandY + bandH + 10;

    // Record types within this sport
    const typeEntries = Object.entries(group.types);
    for (const [typeName, rows] of typeEntries) {
      if (cursorY > pageHeight - 80) { doc.addPage(); cursorY = 40; }

      // Record Type badge
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...sportPrimary);
      const badgeText = typeName.toUpperCase();
      const badgeW = doc.getTextWidth(badgeText) + 14;
      doc.setFillColor(...sportAccent);
      doc.rect(marginX, cursorY - 2, badgeW, 16, "F");
      doc.text(badgeText, marginX + 7, cursorY + 9);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(120, 120, 120);
      doc.text(`${rows.length} record${rows.length === 1 ? "" : "s"}`, marginX + badgeW + 8, cursorY + 9);
      cursorY += 20;

      // Dynamic column set from CSV import — union of keys in visible rows.
      const cols = (() => {
        const seen = new Set(); const out = [];
        rows.forEach(r => Object.keys(r.dynamic_columns || {}).forEach(c => {
          if (c && !seen.has(c)) { seen.add(c); out.push(c); }
        }));
        return out;
      })();
      if (cols.length === 0) {
        // No schema for this record type — skip the table entirely.
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text("No fields defined for this record type.", marginX, cursorY);
        cursorY += 18;
        doc.setFont("helvetica", "normal");
        continue;
      }

      // Table body
      const body = rows.map(r => [
        r.category || "",
        ...cols.map(c => r.dynamic_columns?.[c] ?? ""),
      ]);

      autoTable(doc, {
        startY: cursorY,
        head: [["Category", ...cols]],
        body,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", textColor: [40, 40, 40] },
        headStyles: { fillColor: [242, 242, 242], textColor: sportPrimary, fontStyle: "bold", fontSize: 9 },
        alternateRowStyles: { fillColor: [250, 250, 252] },
        didDrawPage: (data) => {
          // Footer on every page
          const p = doc.internal.getNumberOfPages();
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(`${schoolName}${settings.contact_email ? " · " + settings.contact_email : ""}`, marginX, pageHeight - 30);
          const correctionsLine = settings.contact_email
            ? `See an error? Contact ${settings.contact_email} to suggest a correction.`
            : "See an error? Contact the site administrator to suggest a correction.";
          doc.setTextColor(120, 120, 120);
          doc.setFont("helvetica", "italic");
          doc.text(correctionsLine, marginX, pageHeight - 18);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(150, 150, 150);
          doc.text(`Page ${p}`, pageWidth - marginX, pageHeight - 18, { align: "right" });
        },
      });
      cursorY = doc.lastAutoTable.finalY + 14;
    }
  }

  doc.save(fileName(schoolName, query));
}

function fileName(schoolName, query) {
  const safe = (s) => (s || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();
  const parts = ["records", safe(query) || "search", new Date().toISOString().slice(0, 10)];
  return parts.filter(Boolean).join("_") + ".pdf";
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
